import type { ReactNode } from 'react';
import { Label } from '@monksflow/monks-ui';

/**
 * Layout primitives for the taxonomy form dialogs.
 *
 * Shared rather than per-dialog so the "New field" dialogs in the Fields tab and
 * in the schema builder stay in lockstep — they ask for nearly the same things,
 * and a user moving between them shouldn't meet two different-looking forms.
 */

/** Small caps heading that separates a form into scannable groups. */
export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

export interface FormFieldProps {
  label: string;
  htmlFor?: string;
  /** Lets a control that a `<label>` cannot name, such as a radio group, point at it with `aria-labelledby`. */
  labelId?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * One labelled control. Exists so grid cells stay uniform — every field is the
 * same stack, so a row never looks ragged when one has helper text.
 *
 * `error` replaces `hint` rather than stacking, so the row height doesn't jump
 * when validation fires.
 */
export function FormField({
  label,
  htmlFor,
  labelId,
  hint,
  error,
  required,
  className,
  children,
}: FormFieldProps) {
  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <Label id={labelId} htmlFor={htmlFor}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : (
        hint && <span className="text-xs text-muted-foreground">{hint}</span>
      )}
    </div>
  );
}
